/**
 * TurnRecommendationCard — shows the AI's monthly turn-time reduction suggestions.
 * Fetched from /api/analytics/turn-recommendations. Hidden entirely if throttled
 * (< 30 days since the last one) or if there are no meaningful reductions to suggest.
 * One-click Apply or Not now — the backend records the decision either way so we
 * don't nag the operator until at least 30 days from today.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { TrendUp, CheckCircle, X } from "@phosphor-icons/react";

export default function TurnRecommendationCard() {
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/analytics/turn-recommendations").then((r) => setRec(r.data)).catch(() => setRec({ suggestions: [] }));
  }, []);

  if (!rec || rec.throttled || (!rec.suggestions?.length && !rec.peak_hours?.length)) return null;

  const anySuggestions = rec.suggestions?.length > 0;
  const anyPeaks = rec.peak_hours?.length > 0;

  const apply = async () => {
    setBusy(true);
    try {
      await api.post("/analytics/turn-recommendations/apply", { suggestions: rec.suggestions });
      toast.success(`Applied — ${rec.suggestions.length} turn time${rec.suggestions.length > 1 ? "s" : ""} updated`);
      setRec({ ...rec, suggestions: [] });
    } catch { toast.error("Apply failed"); }
    setBusy(false);
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post("/analytics/turn-recommendations/dismiss");
      setRec({ ...rec, suggestions: [] });
      toast.success("Dismissed — we'll check back in 30 days.");
    } catch { toast.error("Failed"); }
    setBusy(false);
    if (rec.suggestions?.length > 0 || rec.peak_hours?.length > 0) setRec({ ...rec, suggestions: [], peak_hours: [] });
  };

  return (
    <div className="paper-card p-5 mb-6 border-l-4 border-[#1E3A2F] bg-[#C2D1C1]/25" data-testid="turn-recommendation-card">
      <div className="flex items-start gap-3">
        <TrendUp size={22} color="#1E3A2F" weight="fill" className="mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="overline mb-1">Monthly seating-pattern insight</div>
          <h3 className="font-heading font-extrabold text-lg mb-1">
            {anySuggestions ? "Your turn times need a tune-up" : "Peak-hour patterns"}
          </h3>
          {anySuggestions && (
            <>
              <p className="text-sm text-[#5C6B61] leading-snug mb-3">
                Analyzed <b>{rec.sample_size}</b> seated visits across party sizes {rec.party_sizes_analyzed?.join(", ")}. Tightening (or extending) these turn times will make your public wait quotes more accurate.
              </p>
              <div className="paper-card p-3 bg-white mb-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-[#5C6B61] border-b border-[#E1DFD5]">
                      <th className="text-left py-1">Party size</th>
                      <th className="text-right py-1">Currently</th>
                      <th className="text-right py-1">Actual median</th>
                      <th className="text-right py-1">Suggested</th>
                      <th className="text-right py-1">Direction</th>
                      <th className="text-right py-1">Sample</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rec.suggestions.map((s) => (
                      <tr key={s.party_size} className="border-b border-[#F1F1F1] last:border-0" data-testid={`turn-rec-row-${s.party_size}`}>
                        <td className="py-1.5">{s.party_size} top</td>
                        <td className="py-1.5 text-right text-[#5C6B61]">{s.current_minutes} min</td>
                        <td className="py-1.5 text-right">{s.median_actual} min</td>
                        <td className="py-1.5 text-right font-medium text-[#1E3A2F]">{s.suggested_minutes} min</td>
                        <td className="py-1.5 text-right">
                          <span className={`tag ${s.direction === "increase" ? "tag-notified" : "tag-seated"} text-[9px]`}>
                            {s.direction === "increase" ? "↑ raise" : "↓ tighten"}
                          </span>
                        </td>
                        <td className="py-1.5 text-right text-[10px] text-[#5C6B61]">{s.sample_size}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {anyPeaks && (
            <div className="paper-card p-3 bg-white mb-3" data-testid="peak-hours-panel">
              <div className="text-[10px] uppercase tracking-wide text-[#5C6B61] mb-2">Peak nights &amp; hours ({rec.peak_sample_size} guests analyzed)</div>
              <div className="grid grid-cols-3 gap-2">
                {rec.peak_hours.map((p, i) => (
                  <div key={`${p.weekday}-${p.hour}`} className="text-center paper-card p-2 bg-[#F7F6F2]" data-testid={`peak-hour-${i}`}>
                    <div className="font-heading font-extrabold text-base">{p.weekday} {p.hour_label}</div>
                    <div className="text-[10px] text-[#5C6B61]">{p.guest_count} guests · {p.share_pct}% of traffic</div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[#5C6B61] mt-2 leading-snug">
                Staff up these slots — they consistently drive your biggest queue. This is data-only for now; no configuration to accept or dismiss here.
              </p>
            </div>
          )}
          <div className="flex gap-2">
            {anySuggestions && (
              <button onClick={apply} disabled={busy} className="btn-primary !py-2 !px-3 text-sm inline-flex items-center gap-1.5" data-testid="turn-rec-apply">
                <CheckCircle size={14} color="#fff" weight="fill" /> Apply these turn times
              </button>
            )}
            <button onClick={dismiss} disabled={busy} className="btn-ghost !py-2 !px-3 text-sm inline-flex items-center gap-1.5" data-testid="turn-rec-dismiss">
              <X size={14} /> {anySuggestions ? "Not now" : "Got it"}
            </button>
          </div>
          <p className="text-[11px] text-[#5C6B61] mt-2 leading-snug">
            {anySuggestions ? "Reductions and increases are both proposed. You won't see this card again for at least 30 days." : "You won't see this card again for at least 30 days."}
          </p>
        </div>
      </div>
    </div>
  );
}
